import { Button, Modal, Space } from "antd";
import {
	useApproval,
	useResolveApproval,
} from "@/common/artemis-operations/approvals";
import { type Approval, approvalIsActionable } from "@/common/model";
import { CustomDrawer } from "../layout/workspace/custom-drawer";
import { ApprovalDetails } from "./approval-details";

interface ApprovalDetailsDrawerProps {
	approval?: Approval;
	open: boolean;
	onClose: () => void;
	workspaceId: string;
}

export function ApprovalDetailsDrawer({
	approval,
	open,
	onClose,
	workspaceId,
}: ApprovalDetailsDrawerProps) {
	const resolveApproval = useResolveApproval(workspaceId);
	// The row passed in comes from the list query (no credentialRequest); refetch the single approval
	// to get the structured requested actions/resources. Fall back to the row while it loads.
	const { data: fullApproval } = useApproval(approval?.id);
	const { confirm } = Modal;

	if (!approval) return null;
	const details = fullApproval ?? approval;
	// Only a PENDING approval inside its approval window can still be acted on; anything resolved
	// — or timed out — locks the buttons. See approvalIsActionable: expiry is lazy server-side, so
	// a stale request still reads as PENDING here.
	const disabled = resolveApproval.isPending || !approvalIsActionable(details);

	const handleResolve = (decision: "APPROVED" | "REJECTED") => {
		const isApprove = decision === "APPROVED";
		confirm({
			title: isApprove ? "Approve this request?" : "Reject this request?",
			content: isApprove
				? "The agent will be allowed to proceed with the described action."
				: "The agent action will be blocked and the task will be marked as failed.",
			okText: isApprove ? "Approve" : "Reject",
			okButtonProps: { danger: !isApprove },
			onOk: () =>
				resolveApproval.mutate(
					{ approvalId: approval.id, input: { decision } },
					{ onSuccess: onClose },
				),
		});
	};

	return (
		<CustomDrawer
			title={approval.name || approval.id}
			open={open}
			size="large"
			onClose={onClose}
			footer={
				<Space style={{ width: "100%", justifyContent: "flex-end" }}>
					<Button
						type="primary"
						disabled={disabled}
						onClick={() => handleResolve("APPROVED")}
					>
						Approve
					</Button>
					<Button
						danger
						disabled={disabled}
						onClick={() => handleResolve("REJECTED")}
					>
						Reject
					</Button>
				</Space>
			}
		>
			<ApprovalDetails approval={details} />
		</CustomDrawer>
	);
}
